import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cardBackActivitySectionCollapsedPatchSchema,
  cardBackPreferencesSchema,
  commentAnchor,
} from '@molt/shared';
import type {
  ActivityFeed,
  ActivityEntry,
  BoardMemberWithHistory,
  CardBackPreferences,
  Comment,
  CommentStream,
  CommentStreamComment,
  ReactionEmoji,
} from '@molt/shared';

import { Button, Disclosure, EmptyState, Notice, Skeleton } from '@/components';
import { announce } from '@/lib/announce';
import { get, patch } from '@/lib/api';
import {
  cardActivityQueryOptions,
  getCardActivity,
  mergeActivityFeeds,
  type ActivityFilter,
} from '@/lib/activity';
import {
  presignAttachmentUpload,
  putAttachmentBytes,
  useCreateCommentAttachment,
} from '@/lib/attachments';
import { boardContentsQueryOptions } from '@/lib/board-face';
import { useAddBoardMember } from '@/lib/boards';
import {
  commentPermissionMessage,
  mentionCandidates,
  getCommentStream,
  commentStreamQueryOptions,
  groupMentionOptions,
  useCreateComment,
  useDeleteComment,
  useUpdateComment,
  quoteCommentBody,
} from '@/lib/comments';
import type { CommentTypingEntry } from '@/lib/comment-typing';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useToggleCommentReaction } from '@/lib/comment-reactions';
import { useSetWatchState, watchStateQueryOptions } from '@/lib/watch';
import {
  absolutePermalink,
  copyTextToClipboard,
  getActivityPermalink,
  getCommentPermalink,
  parsePermalinkHash,
  permalinkPath,
} from '@/lib/permalinks';
import { queryKeys } from '@/lib/query-keys';
import { workspaceMembersQueryOptions } from '@/lib/workspaces';
import {
  CommentComposer,
  type CommentBoardLinkOption,
  type CommentCardLinkOption,
  type CommentComposerSubmit,
} from './CommentComposer';
import { CommentItem } from './CommentItem';
import { ActivityEntryItem } from './ActivityEntryItem';
import { CommentTypingIndicator } from './CommentTypingIndicator';

export interface CommentSectionProps {
  readonly cardId: string;
  readonly boardId: string;
  readonly workspaceId?: string | undefined;
  readonly boardClosed: boolean;
  readonly readOnlyReason?: { readonly title: string; readonly body: string } | undefined;
  readonly boardMembers: readonly BoardMemberWithHistory[];
  readonly cardMembers: readonly { id: string }[];
  readonly currentUserId?: string | undefined;
  /**
   * `isSuperadminSession(session)` — SA-9. Optional, and absent reads as `false`,
   * so a surface that does not know fails closed.
   */
  readonly currentUserIsSuper?: boolean | undefined;
  readonly typingUsers?: readonly CommentTypingEntry[] | undefined;
  readonly onTypingStateChange?: ((typing: boolean) => void) | undefined;
  readonly newActivityEntryIds?: ReadonlySet<string> | undefined;
  readonly onAcknowledgeNewActivityEntries?: (() => void) | undefined;
  /**
   * A `/c/<ref>/com/<n>` address's comment number — `comments.number`, per
   * card (`docs/notes/comment-numbers.md`). Scrolled to and highlighted like a
   * `#comment-<id>` hash, which wins when both are present. Consumed **once**:
   * unlike a hash, nothing in the URL changes when the reader moves on, so a
   * live comment arriving later must not drag the view back to it.
   */
  readonly targetCommentNumber?: number | undefined;
}

const COLLAPSED_KEY = 'molt:card-comments-collapsed';
const DETAILS_HIDDEN_KEY = 'molt:card-activity-details-hidden';
const PAGE_SIZE = 25;

function readCollapsedPreference(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const storage = window.localStorage;
    if (typeof storage.getItem !== 'function') return false;
    return storage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsedPreference(collapsed: boolean): void {
  try {
    if (typeof window === 'undefined') return;
    const storage = window.localStorage;
    if (typeof storage.setItem !== 'function') return;
    storage.setItem(COLLAPSED_KEY, collapsed ? 'true' : 'false');
  } catch {
    // Local-only convenience state; unavailable storage must not break the card back.
  }
}

/**
 * `GET`/`PATCH /me/card-back-preferences` — `FR-CARD-034`'s collapse as an
 * **account** preference (`FE-16.1`), over the two routes `BE-14.5` shipped.
 *
 * ⚠ **Declared in this component file rather than in a new `@/lib/` module, and
 * that is a scope decision rather than a preference.** This chunk's declared web
 * file list is `CommentSection.tsx` alone and eleven sibling chunks are editing
 * the same tree. `CardBack.tsx` set the precedent one chunk earlier with
 * `cardDescriptionDraftPath`/`fetchCardDescriptionDraft`/`storeCardDescriptionDraft`
 * — the neighbouring half of the same `BE-14.5` contract — and records the same
 * reason. A follow-up that owns `apps/web/src/lib/` should lift both sets out
 * together, beside `cards.ts`.
 */
const CARD_BACK_PREFERENCES_PATH = '/me/card-back-preferences';

function fetchCardBackPreferences(): Promise<CardBackPreferences> {
  return get(CARD_BACK_PREFERENCES_PATH, cardBackPreferencesSchema);
}

/**
 * SC-4, one route per field: the body is exactly `{ activitySectionCollapsed }`
 * and the strict schema parses **before** the request goes out, so a future
 * caller that tried to set `hideActivityDetails` here fails in the client rather
 * than taking a 422 from a route that would never have written it anyway.
 */
function storeActivitySectionCollapsed(
  activitySectionCollapsed: boolean,
): Promise<CardBackPreferences> {
  return patch(
    `${CARD_BACK_PREFERENCES_PATH}/activity-section-collapsed`,
    cardBackPreferencesSchema,
    cardBackActivitySectionCollapsedPatchSchema.parse({ activitySectionCollapsed }),
  );
}

function readDetailsHiddenPreference(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const storage = window.localStorage;
    if (typeof storage.getItem !== 'function') return false;
    return storage.getItem(DETAILS_HIDDEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeDetailsHiddenPreference(hidden: boolean): void {
  try {
    if (typeof window === 'undefined') return;
    const storage = window.localStorage;
    if (typeof storage.setItem !== 'function') return;
    storage.setItem(DETAILS_HIDDEN_KEY, hidden ? 'true' : 'false');
  } catch {
    // Local-only bridge until B28's stored preference route exists.
  }
}

export function CommentSection({
  cardId,
  boardId,
  workspaceId,
  boardClosed,
  readOnlyReason,
  boardMembers,
  cardMembers,
  currentUserId,
  currentUserIsSuper = false,
  typingUsers = [],
  onTypingStateChange,
  newActivityEntryIds,
  onAcknowledgeNewActivityEntries,
  targetCommentNumber,
}: CommentSectionProps): ReactElement {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const signedIn = currentUserId !== undefined;
  const activity = useQuery({
    ...cardActivityQueryOptions(cardId, filter, PAGE_SIZE),
    enabled: signedIn,
  });
  const permissionStream = useQuery(
    commentStreamQueryOptions(cardId, signedIn ? 1 : PAGE_SIZE),
  );
  /*
   * ⚠⚠ **THE SECOND, CLAMPED COMMENT READ USED TO STAND HERE. ITS DELETION IS
   * WHAT `FE-70.4` CLOSES — the field is not the point, the deleted query is.**
   *
   * `authorPresence` and `via` (`FE-16.2` / `FR-COLLAB-006` AC4, `FE-70.4` /
   * `FR-AUTO-016` AC3) lived only on `commentStreamCommentSchema`, while a
   * signed-in reader's comments arrive on `ActivityEntry.comment`, which was
   * the **base** `commentSchema`. So the two facts had to be fetched a second
   * time out of `GET /cards/:id/comments` and merged back on by hand — and that
   * read was bounded by `commentStreamQuerySchema.limit`'s `.max(100)`, i.e.
   * `COMMENT_PAGE_SIZE_MAX`. Past a hundred comments loaded into one card back
   * the map simply had no key, and every older row rendered with **no presence
   * at all** while the request still answered 200.
   *
   * `activityEntrySchema.comment` is now `commentStreamCommentSchema` and
   * `apps/server/src/activity/hydrate.ts` projects both fields, so `entry.comment`
   * carries them at every depth of the feed. **Comment-data queries for a
   * signed-in reader: 3 → 2** — the feed and the `limit: 1` permission probe
   * remain; the clamped third is gone, and with it `attributeComment`,
   * `attributionLimitFor`, `commentAttributionMap`,
   * `commentAttributionQueryOptions` and the `CommentAttribution` type.
   *
   * ⚠ The `placeholderData` that kept every deactivated-style avatar from
   * blinking off at load-more went with it, and needs no replacement: the page
   * width is no longer in any query key, because there is no longer a query
   * whose width depends on how much of the feed is on screen.
   */
  const boardContents = useQuery(boardContentsQueryOptions(boardId));
  const workspaceMembers = useQuery({
    ...workspaceMembersQueryOptions(workspaceId ?? ''),
    enabled: signedIn && workspaceId !== undefined,
    retry: false,
  });
  const createComment = useCreateComment();
  const createCommentAttachment = useCreateCommentAttachment();
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();
  const toggleReaction = useToggleCommentReaction();
  const setWatchState = useSetWatchState();
  const addBoardMember = useAddBoardMember(boardId);
  /**
   * `FR-CARD-034`: *"the preference persists **per user** across cards"*, and
   * AC2 — `FE-16.1`. The one piece of this section's state that belongs to the
   * ACCOUNT rather than to the browser, and the reason this chunk exists: until
   * now `molt:card-comments-collapsed` kept the promise on **one** browser,
   * which is cross-card persistence, not per-user persistence.
   *
   * `enabled: signedIn` — a signed-out reader has no `/me` to read and keeps
   * exactly today's local-only behaviour. Nothing gates the section's skeleton,
   * error branch or content on this query: it arrives late, the collapse
   * corrects itself, and a failure leaves the local value standing.
   */
  const storedCollapsePreference = useQuery({
    queryKey: queryKeys.meCardBackPreferences(),
    queryFn: fetchCardBackPreferences,
    enabled: signedIn,
  });
  /**
   * ⚠⚠ **THE INITIALISER READS THE CACHE FIRST AND `localStorage` SECOND, AND
   * NEITHER HALF IS OPTIONAL.**
   *
   * A member who keeps this section collapsed must never watch it flash open.
   * If `open` started at `true` and waited for the round trip, it would flash on
   * **every card back they open** — the product tie-breaker turned against the
   * feature, with nothing red anywhere, because no unit test can see a frame.
   *
   *   - The **cache** (`['me', 'card-back-preferences']`) is filled on every
   *     card back after the first in a session, so reading it synchronously here
   *     means the second card back's first paint is already the ACCOUNT's answer
   *     — not even one frame of the browser-local value.
   *   - **`localStorage`** covers the only case the cache cannot: the first card
   *     back of a fresh page load, while the read is in flight. It is a seed, not
   *     a source of truth; the response overwrites it below.
   */
  const [open, setOpen] = useState(() => {
    const cached = queryClient.getQueryData<CardBackPreferences>(
      queryKeys.meCardBackPreferences(),
    );
    return !(cached?.activitySectionCollapsed ?? readCollapsedPreference());
  });
  /**
   * Set by the hydration effect **and** by `setSectionOpen`, which is what makes
   * a deliberate click beat a slow read: without it, a member who collapses the
   * section before the first response lands would watch their own click undone a
   * moment later by a value that predates it.
   */
  const collapseHydratedRef = useRef(false);
  /**
   * The account write. `onSuccess` seeds the cache from the route's own 200 body
   * rather than from the value that was sent, so the next card back's
   * initialiser reads what the database holds.
   *
   * A failure is deliberately silent and shows no notice: the local key still
   * carries the choice for this card back, and the account's value takes it back
   * on the next mount. That is the honest consequence of making the account the
   * source of truth — a local value that outlived a failed write would be
   * exactly the "saved in this browser only" bug this chunk removes.
   */
  const setCollapsePreference = useMutation({
    mutationFn: storeActivitySectionCollapsed,
    onSuccess: (preferences) => {
      queryClient.setQueryData(queryKeys.meCardBackPreferences(), preferences);
    },
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [attachmentPosting, setAttachmentPosting] = useState(false);
  const [watchOfferVisible, setWatchOfferVisible] = useState(false);
  const watchOfferRef = useRef<HTMLDivElement | null>(null);
  const [highlightedTarget, setHighlightedTarget] = useState<string | null>(null);
  const [deletedCommentVisible, setDeletedCommentVisible] = useState(false);
  const [locationHash, setLocationHash] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.hash,
  );
  const [draftSeed, setDraftSeed] = useState<
    { readonly id: string; readonly body: string } | undefined
  >(undefined);
  const currentMember = boardMembers.find((member) => member.id === currentUserId);
  const currentUserOnCard =
    currentUserId !== undefined &&
    cardMembers.some((member) => member.id === currentUserId);
  const cardWatch = useQuery(
    watchStateQueryOptions(
      { targetType: 'card', targetId: cardId },
      signedIn && !currentUserOnCard,
    ),
  );
  const groupMentions = useMemo(
    () =>
      groupMentionOptions({
        t,
        boardMemberCount: boardMembers.length,
        cardMemberCount: cardMembers.length,
        currentUserId,
        currentUserOnCard,
      }),
    [t, boardMembers.length, cardMembers.length, currentUserId, currentUserOnCard],
  );
  const individualMentionCandidates = useMemo(
    () =>
      mentionCandidates({
        t,
        boardMembers,
        cardMembers,
        workspaceMembers: workspaceMembers.data,
      }),
    [t, boardMembers, cardMembers, workspaceMembers.data],
  );
  const cardLinkOptions = useMemo<readonly CommentCardLinkOption[]>(() => {
    return (boardContents.data?.lists ?? []).flatMap((list) =>
      list.cards
        .filter((card) => card.id !== cardId)
        .map((card) => ({
          id: card.id,
          title: card.title,
          boardTitle: boardContents.data?.board.title,
          listTitle: list.title,
        })),
    );
  }, [boardContents.data, cardId]);
  const boardLinkOptions = useMemo<readonly CommentBoardLinkOption[]>(() => {
    const board = boardContents.data?.board;
    return board === undefined ? [] : [{ id: board.id, title: board.title }];
  }, [boardContents.data?.board]);
  /*
   * Memoised so a signed-out reader's feed keeps its identity across renders:
   * it is a dependency of the permalink effect, which would otherwise re-run on
   * every render.
   */
  const signedOutFeed = useMemo(
    () =>
      permissionStream.data === undefined
        ? undefined
        : commentsToActivityFeed(permissionStream.data),
    [permissionStream.data],
  );
  const data = signedIn ? activity.data : signedOutFeed;
  const permissionData = permissionStream.data;
  const message =
    readOnlyReason ??
    (permissionData === undefined
      ? null
      : commentPermissionMessage(
          t,
          permissionData,
          currentUserId !== undefined,
          boardClosed,
        ));
  const canReact =
    readOnlyReason === undefined && permissionData?.mayComment === true && !boardClosed;
  const canRemoveOwnReaction =
    currentUserId !== undefined &&
    readOnlyReason === undefined &&
    permissionData !== undefined &&
    !boardClosed &&
    (permissionData.mayComment || permissionData.commentingPermission === 'disabled');
  /** The cursor of the page a permalink target is being looked for on. */
  const loadingPermalinkTarget = useRef<string | null>(null);
  const consumedCommentNumber = useRef<number | undefined>(undefined);
  const hasNewActivityEntries = (newActivityEntryIds?.size ?? 0) > 0;
  // `readCollapsedPreference()` used to seed `open` from here. It now seeds the
  // `useState` initialiser instead — an effect runs after the first paint, so
  // this was the flash it was meant to prevent. `hide_activity_details` still
  // has no route of its own (`FR-COLLAB-019`, unowned), so its half stays local.
  useEffect(() => {
    setFilter(readDetailsHiddenPreference() ? 'comments' : 'all');
  }, []);

  /**
   * The account's answer, applied **once per mount**.
   *
   * The guard is what stops a late first response from clobbering a click the
   * member has already made (see `collapseHydratedRef`). The `localStorage`
   * mirror is what makes the NEXT fresh page load seed from the account rather
   * than from whatever this browser last remembered on its own.
   */
  useEffect(() => {
    const preferences = storedCollapsePreference.data;
    if (preferences === undefined || collapseHydratedRef.current) return;
    collapseHydratedRef.current = true;
    writeCollapsedPreference(preferences.activitySectionCollapsed);
    setOpen(!preferences.activitySectionCollapsed);
  }, [storedCollapsePreference.data]);

  useEffect(() => {
    return () => onTypingStateChange?.(false);
  }, [onTypingStateChange]);

  useEffect(() => {
    if (!watchOfferVisible) return;
    requestAnimationFrame(() => {
      watchOfferRef.current?.scrollIntoView({ block: 'nearest' });
    });
  }, [watchOfferVisible]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateHash = () => setLocationHash(window.location.hash);
    window.addEventListener('hashchange', updateHash);
    return () => window.removeEventListener('hashchange', updateHash);
  }, []);

  useEffect(() => {
    if (highlightedTarget === null || typeof window === 'undefined') return undefined;
    const timer = window.setTimeout(() => setHighlightedTarget(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [highlightedTarget]);

  useEffect(() => {
    if (data === undefined || typeof window === 'undefined') return undefined;
    const parsed = parsePermalinkHash(locationHash);
    /*
     * A hash target wins; a comment number is the fallback, and only until it
     * has been found (or looked for to the end of the stream) once.
     */
    const commentNumber =
      parsed === null &&
      targetCommentNumber !== undefined &&
      consumedCommentNumber.current !== targetCommentNumber
        ? targetCommentNumber
        : undefined;
    if (parsed === null && commentNumber === undefined) return undefined;

    const matchingEntry = data.entries.find((entry) => {
      if (parsed === null) return entry.comment?.number === commentNumber;
      if (parsed.target === 'comment') return entry.comment?.id === parsed.id;
      return entry.comment === null && entry.id === parsed.id;
    });

    if (matchingEntry !== undefined) {
      const targetId =
        parsed === null && matchingEntry.comment !== null
          ? commentAnchor(matchingEntry.comment.id)
          : locationHash.startsWith('#')
            ? locationHash.slice(1)
            : locationHash;
      if (commentNumber !== undefined) consumedCommentNumber.current = commentNumber;
      setDeletedCommentVisible(false);
      setOpen(true);
      setHighlightedTarget(targetId);
      requestAnimationFrame(() => {
        document.getElementById(targetId)?.scrollIntoView?.({
          block: 'center',
          behavior: 'smooth',
        });
      });
      return undefined;
    }

    if (parsed?.target === 'activity' && signedIn && filter === 'comments') {
      setFilter('all');
      return undefined;
    }

    if (data.nextCursor !== null) {
      /*
       * ⚠ **The target is not on the loaded pages, and more pages exist: load
       * the next one and wait — never conclude anything yet.** The in-flight
       * page is tracked by its cursor and NOT cancelled when the effect re-runs:
       * a re-run is routine while it is in flight (a refetch, a live frame, and
       * for a signed-out reader any re-render), and the old cancel-on-cleanup
       * left the flag stuck, so the next run fell through to the "whole stream
       * loaded" branches below on a partial stream — for a number, a false
       * deleted-comment notice and a target never scrolled to.
       */
      if (loadingPermalinkTarget.current !== null) return undefined;
      const cursor = data.nextCursor;
      loadingPermalinkTarget.current = cursor;
      const release = () => {
        if (loadingPermalinkTarget.current === cursor) {
          loadingPermalinkTarget.current = null;
        }
      };
      const loadTargetPage = signedIn
        ? getCardActivity(cardId, {
            filter,
            cursor,
            limit: PAGE_SIZE,
          }).then((next) => {
            release();
            queryClient.setQueryData<ActivityFeed>(
              queryKeys.cards.activity(cardId, filter),
              (current) => mergeActivityFeeds(current, next),
            );
          })
        : getCommentStream(cardId, {
            cursor,
            limit: PAGE_SIZE,
          }).then((next) => {
            release();
            queryClient.setQueryData<CommentStream>(
              queryKeys.cards.comments(cardId),
              (current) =>
                current === undefined
                  ? next
                  : {
                      ...next,
                      comments: [...current.comments, ...next.comments],
                    },
            );
          });

      void loadTargetPage.catch(() => undefined).finally(release);
      return undefined;
    }

    if (commentNumber !== undefined) {
      /*
       * The whole stream is loaded and the number is not in it. Numbers are
       * never reused, so a LATER number on the card means this one was
       * deleted — the same notice a deleted `#comment-<id>` gets. With no later
       * number there is nothing to say: it may be the newest comment, deleted,
       * or one that was never posted, and claiming either would be a guess.
       */
      consumedCommentNumber.current = commentNumber;
      const deleted = data.entries.some(
        (entry) => entry.comment !== null && entry.comment.number > commentNumber,
      );
      if (deleted) {
        setOpen(true);
        setDeletedCommentVisible(true);
      }
      return undefined;
    }

    if (parsed?.target !== 'comment') return undefined;

    let cancelled = false;
    void getCommentPermalink(parsed.id)
      .then((response) => {
        if (!cancelled && response.targetStatus === 'deleted') {
          setOpen(true);
          setDeletedCommentVisible(true);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [cardId, data, filter, locationHash, queryClient, signedIn, targetCommentNumber]);

  /**
   * Both stores, in that order, and the local one unconditionally.
   *
   * The local write is not a fallback — it is the pre-hydration seed the next
   * fresh page load reads before the query resolves, so it must happen even
   * when the account write is about to succeed. The account write is what makes
   * `FR-CARD-034` AC2 follow the person to a second browser.
   */
  function setSectionOpen(next: boolean) {
    collapseHydratedRef.current = true;
    setOpen(next);
    writeCollapsedPreference(!next);
    if (signedIn) setCollapsePreference.mutate(!next);
  }

  function setDetailsHidden(hidden: boolean) {
    const nextFilter: ActivityFilter = hidden ? 'comments' : 'all';
    setFilter(nextFilter);
    writeDetailsHiddenPreference(hidden);
    if (!hidden) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.activity(cardId, 'all'),
      });
    }
  }

  function invalidateActivity() {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.cards.activity(cardId, 'all'),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.cards.activity(cardId, 'comments'),
    });
  }

  async function loadMore() {
    if (data?.nextCursor === null || data?.nextCursor === undefined) return;
    setLoadingMore(true);
    try {
      if (!signedIn) {
        const next = await getCommentStream(cardId, {
          cursor: data.nextCursor,
          limit: PAGE_SIZE,
        });
        queryClient.setQueryData<CommentStream>(
          queryKeys.cards.comments(cardId),
          (current) =>
            current === undefined
              ? next
              : {
                  ...next,
                  comments: [...current.comments, ...next.comments],
                },
        );
        return;
      }

      const next = await getCardActivity(cardId, {
        filter,
        cursor: data.nextCursor,
        limit: PAGE_SIZE,
      });
      queryClient.setQueryData<ActivityFeed>(
        queryKeys.cards.activity(cardId, filter),
        (current) => mergeActivityFeeds(current, next),
      );
    } finally {
      setLoadingMore(false);
    }
  }

  function canDeleteComment(comment: Comment): boolean {
    if (comment.author?.id === currentUserId) return true;
    // SA-9: mirrors the server's `mayDeleteComment`, whose `actorIsSuper` arm
    // enters this admin branch with the same author allow-list below.
    if (currentMember?.role !== 'admin' && !currentUserIsSuper) return false;
    if (comment.author === null) return true;
    const authorMember = boardMembers.find((member) => member.id === comment.author?.id);
    const authorRole = authorMember?.role ?? null;
    return authorRole === null || authorRole === 'normal' || authorRole === 'observer';
  }

  async function submitComment(input: CommentComposerSubmit): Promise<void> {
    const wasWatchingBeforeSubmit = cardWatch.data?.watching;
    const comment = await createComment.mutateAsync({
      boardId,
      cardId,
      body: input.body,
    });
    if (signedIn && !currentUserOnCard && wasWatchingBeforeSubmit === false) {
      setWatchOfferVisible(true);
    }

    if (input.attachments.length === 0) {
      invalidateActivity();
      return;
    }

    setAttachmentPosting(true);
    try {
      const failed: string[] = [];
      for (const attachment of input.attachments) {
        try {
          if (attachment.kind === 'file') {
            if (attachment.file === undefined) {
              failed.push(attachment.displayName);
              continue;
            }
            const presign = await presignAttachmentUpload(cardId, {
              fileName: attachment.file.name,
              sizeBytes: attachment.file.size,
              contentType: attachment.file.type || 'application/octet-stream',
            });
            await putAttachmentBytes(t, presign, attachment.file);
            await createCommentAttachment.mutateAsync({
              commentId: comment.id,
              cardId,
              boardId,
              body: {
                kind: 'file',
                storageKey: presign.storageKey,
                displayName: attachment.displayName.trim() || null,
              },
            });
          } else if (
            attachment.kind === 'card_link' &&
            attachment.targetCardId !== undefined
          ) {
            await createCommentAttachment.mutateAsync({
              commentId: comment.id,
              cardId,
              boardId,
              body: { kind: 'card_link', cardId: attachment.targetCardId },
            });
          } else if (
            attachment.kind === 'board_link' &&
            attachment.targetBoardId !== undefined
          ) {
            await createCommentAttachment.mutateAsync({
              commentId: comment.id,
              cardId,
              boardId,
              body: { kind: 'board_link', boardId: attachment.targetBoardId },
            });
          }
        } catch {
          failed.push(attachment.displayName);
        }
      }

      if (failed.length > 0) {
        announce(
          t(
            'cardBack.comments.announce.attachmentsFailed',
            { count: failed.length },
            failed.length,
          ),
        );
      }
    } finally {
      setAttachmentPosting(false);
      invalidateActivity();
    }
  }

  async function copyCommentLink(commentId: string): Promise<void> {
    try {
      const response = await getCommentPermalink(commentId);
      const copied = await copyTextToClipboard(
        absolutePermalink(permalinkPath(t, response)),
      );
      announce(
        t(
          copied
            ? 'cardBack.comments.announce.commentLinkCopied'
            : 'cardBack.comments.announce.commentLinkCopyFailed',
        ),
      );
    } catch {
      announce(t('cardBack.comments.announce.commentLinkCopyFailed'));
    }
  }

  async function copyActivityLink(activityId: string): Promise<void> {
    try {
      const response = await getActivityPermalink(activityId);
      const copied = await copyTextToClipboard(
        absolutePermalink(permalinkPath(t, response)),
      );
      announce(
        t(
          copied
            ? 'cardBack.comments.announce.activityLinkCopied'
            : 'cardBack.comments.announce.activityLinkCopyFailed',
        ),
      );
    } catch {
      announce(t('cardBack.comments.announce.activityLinkCopyFailed'));
    }
  }

  function quoteReply(comment: Comment, selectedText?: string): void {
    setDraftSeed({
      id: `${comment.id}:${Date.now().toString()}`,
      body: quoteCommentBody({
        body: comment.body,
        authorUsername: comment.author?.username,
        selection: selectedText,
      }),
    });
  }

  function toggleCommentReaction(input: {
    readonly commentId: string;
    readonly emoji: ReactionEmoji;
    readonly reactedByMe: boolean;
  }): void {
    toggleReaction.mutate(
      {
        cardId,
        commentId: input.commentId,
        emoji: input.emoji,
        action: input.reactedByMe ? 'remove' : 'add',
      },
      {
        onSuccess: () => {
          announce(
            t(
              input.reactedByMe
                ? 'cardBack.comments.announce.reactionRemoved'
                : 'cardBack.comments.announce.reactionAdded',
            ),
          );
        },
      },
    );
  }

  function acceptWatchOffer(): void {
    setWatchState.mutate(
      { targetType: 'card', targetId: cardId, watching: true },
      {
        onSuccess: () => setWatchOfferVisible(false),
      },
    );
  }

  function acknowledgeNewActivityEntries(): void {
    if (!hasNewActivityEntries) return;
    onAcknowledgeNewActivityEntries?.();
  }

  return (
    <section
      aria-labelledby="card-comments-heading"
      className="flex flex-col gap-3"
      onFocusCapture={acknowledgeNewActivityEntries}
      onKeyDownCapture={acknowledgeNewActivityEntries}
      onPointerDownCapture={acknowledgeNewActivityEntries}
    >
      <h2 id="card-comments-heading" className="sr-only">
        {t('cardBack.comments.heading')}
      </h2>
      <Disclosure
        open={open}
        onOpenChange={setSectionOpen}
        summary={t('cardBack.comments.heading')}
        {...(data === undefined ? {} : { count: data.entries.length })}
      >
        <div className="flex flex-col gap-3">
          {permissionStream.isLoading || (signedIn && activity.isLoading) ? (
            <Skeleton shape="block" count={3} />
          ) : permissionStream.isError ||
            (signedIn && activity.isError) ||
            data === undefined ? (
            <Notice
              tone="bad"
              title={t(
                signedIn
                  ? 'cardBack.comments.error.activityTitle'
                  : 'cardBack.comments.error.commentsTitle',
              )}
              body={t('cardBack.comments.error.body')}
            />
          ) : (
            <>
              {signedIn ? (
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="quiet"
                    aria-pressed={filter === 'comments'}
                    onClick={() => setDetailsHidden(filter === 'all')}
                  >
                    {t(
                      filter === 'all'
                        ? 'cardBack.comments.hideDetails'
                        : 'cardBack.comments.showDetails',
                    )}
                  </Button>
                </div>
              ) : null}
              {message === null ? (
                <>
                  <CommentTypingIndicator typingUsers={typingUsers} />
                  <CommentComposer
                    busy={createComment.isPending || attachmentPosting}
                    attachmentBusy={
                      attachmentPosting || createCommentAttachment.isPending
                    }
                    mentionCandidates={individualMentionCandidates}
                    groupMentions={groupMentions}
                    cardLinkOptions={cardLinkOptions}
                    boardLinkOptions={boardLinkOptions}
                    addMentionCandidateBusyId={
                      addBoardMember.isPending
                        ? individualMentionCandidates.find(
                            (candidate) =>
                              addBoardMember.variables !== undefined &&
                              'username' in addBoardMember.variables &&
                              candidate.username === addBoardMember.variables.username,
                          )?.id
                        : undefined
                    }
                    onAddMentionCandidate={async (candidate) => {
                      await addBoardMember.mutateAsync({ username: candidate.username });
                    }}
                    draftSeed={draftSeed}
                    onTypingStateChange={onTypingStateChange}
                    onSubmit={(input) => {
                      void submitComment(input);
                    }}
                  />
                  {watchOfferVisible && cardWatch.data?.watching === false ? (
                    <div ref={watchOfferRef}>
                      <Notice
                        tone="info"
                        title={t('cardBack.comments.watchOffer.title')}
                        body={t('cardBack.comments.watchOffer.body')}
                        action={
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              busy={setWatchState.isPending}
                              busyLabel={t('cardBack.comments.watchOffer.busyLabel')}
                              onClick={acceptWatchOffer}
                            >
                              {t('cardBack.comments.watchOffer.accept')}
                            </Button>
                            <Button
                              size="sm"
                              variant="quiet"
                              onClick={() => setWatchOfferVisible(false)}
                            >
                              {t('cardBack.comments.watchOffer.dismiss')}
                            </Button>
                          </div>
                        }
                      />
                    </div>
                  ) : null}
                </>
              ) : (
                <Notice tone="locked" title={message.title} body={message.body} />
              )}
              {deletedCommentVisible ? (
                <Notice
                  tone="warn"
                  title={t('cardBack.comments.deletedNotice.title')}
                  body={t('cardBack.comments.deletedNotice.body')}
                />
              ) : null}
              {data.entries.length === 0 ? (
                <EmptyState
                  tone="neutral"
                  size="inline"
                  title={t(
                    filter === 'comments'
                      ? 'cardBack.comments.empty.commentsTitle'
                      : 'cardBack.comments.empty.activityTitle',
                  )}
                  body={t(
                    filter === 'comments'
                      ? 'cardBack.comments.empty.commentsBody'
                      : 'cardBack.comments.empty.activityBody',
                  )}
                />
              ) : (
                <div>
                  {data.entries.map((entry) => {
                    if (entry.comment === null) {
                      return (
                        <ActivityEntryItem
                          key={entry.id}
                          entry={entry}
                          highlighted={highlightedTarget === `activity-${entry.id}`}
                          newArrival={newActivityEntryIds?.has(entry.id) === true}
                          onCopyLink={(activityId) => {
                            void copyActivityLink(activityId);
                          }}
                        />
                      );
                    }
                    // `entry.comment` is a `CommentStreamComment` in its own
                    // right now — `authorPresence` and `via` ride the feed at
                    // every depth, so there is nothing left to rejoin and no
                    // clamp to fall off the end of. A signed-out reader reaches
                    // the same shape by the other door: `commentToActivityEntry`
                    // wraps stream rows that already carry both fields.
                    const comment = entry.comment;
                    return (
                      <CommentItem
                        key={entry.id}
                        comment={comment}
                        viaSuperadmin={entry.viaSuperadmin === true}
                        currentUserId={currentUserId}
                        mentionMembers={boardMembers}
                        canDelete={canDeleteComment(comment)}
                        updateBusy={updateComment.isPending}
                        deleteBusy={deleteComment.isPending}
                        highlighted={highlightedTarget === `comment-${comment.id}`}
                        newArrival={newActivityEntryIds?.has(entry.id) === true}
                        canReact={canReact}
                        canRemoveOwnReaction={canRemoveOwnReaction}
                        reactionBusyEmoji={
                          toggleReaction.isPending &&
                          toggleReaction.variables?.commentId === comment.id
                            ? toggleReaction.variables.emoji
                            : undefined
                        }
                        onUpdate={(commentId, body) =>
                          updateComment.mutate(
                            { cardId, commentId, body },
                            { onSuccess: invalidateActivity },
                          )
                        }
                        onDelete={(commentId) =>
                          deleteComment.mutate(
                            { boardId, cardId, commentId },
                            { onSuccess: invalidateActivity },
                          )
                        }
                        onCopyLink={(commentId) => {
                          void copyCommentLink(commentId);
                        }}
                        onReply={quoteReply}
                        onToggleReaction={toggleCommentReaction}
                      />
                    );
                  })}
                </div>
              )}
              {data.nextCursor !== null ? (
                <button
                  type="button"
                  className="self-start rounded-card px-2 py-1 text-sm font-medium text-accent hover:underline"
                  disabled={loadingMore}
                  onClick={() => void loadMore()}
                >
                  {loadingMore
                    ? signedIn
                      ? t('cardBack.comments.loadingActivity')
                      : t('cardBack.comments.loadingComments')
                    : signedIn
                      ? t('cardBack.comments.loadMoreActivity')
                      : t('cardBack.comments.loadMoreComments')}
                </button>
              ) : null}
            </>
          )}
        </div>
      </Disclosure>
    </section>
  );
}

function commentsToActivityFeed(
  stream: Pick<CommentStream, 'comments' | 'nextCursor'>,
): ActivityFeed {
  return {
    entries: stream.comments.map((comment) => commentToActivityEntry(comment)),
    nextCursor: stream.nextCursor,
  };
}

/**
 * ⚠ **`actorAutomationName` is the comment's own `via`**, so a signed-out
 * reader's synthesised entry carries the same provenance a signed-in reader's
 * real `comment.added` entry does — at the *entry* level, where
 * `ActivityEntryItem` reads it to phrase a sentence.
 *
 * ⚠ **It is no longer what renders the comment's own marker, and that is the
 * change `FE-70.4` made.** `comment` is passed through whole, and it is a
 * `CommentStreamComment`, so `via` and `authorPresence` reach `CommentItem` off
 * the comment itself on both paths — out of `hydrate.ts` for a signed-in reader
 * and out of the stream row for a signed-out one. The two paths agree because
 * they read the same field, not because a fallback reconciles them.
 */
function commentToActivityEntry(comment: CommentStreamComment): ActivityEntry {
  return {
    id: `comment:${comment.id}`,
    type: 'comment.added',
    templateKey: 'comment.added',
    tokens: {},
    actor: comment.author,
    actorAutomationName: comment.via ?? null,
    createdAt: comment.createdAt,
    card: null,
    comment,
    fieldOverride: null,
    collapsedCount: 1,
  };
}
